import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress,
  Alert,
  TableContainer,
  Paper,
  IconButton,
  Link,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Router as OnuIcon,
  FilterList as FilterIcon,
  DeleteOutline as DesprovisionIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import OnuDuplicadasModal from './components/OnuDuplicadasModal';
import OnusModal from './olt/OnusModal';
import { toast } from 'react-toastify';
import { Link as RouterLink } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../services/useApi';
import '../../styles/swal-overrides.css';

interface ONU {
  id: number;
  onu_id: number;
  serialNumber: string;
  description?: string;
  descricao?: string;
  portId?: number;
  portNumber?: string;
  slot?: number;
  sfp?: string;
  cardId?: number;
  olt_id?: number;
  olt_name?: string;
  services_port?: number | string;
  txpower?: number;
  rxpower?: number;
  status?: string;
  distance?: number;
  ipv4_address?: string;
  cliente?: { cliente_id: number; nome: string; tituloContrato: string };
  equipamento?: { id: number; marca: string; modelo: string; gpon_sn: string | null };
}

interface OnusRegistradasTabProps {
  refreshTrigger?: number;
}

const OnusRegistradasTab: React.FC<OnusRegistradasTabProps> = ({ refreshTrigger = 0 }) => {
  const theme = useTheme();
  const primario = theme.palette.primary.main;
  const [onus, setOnus] = useState<ONU[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterSerial, setFilterSerial] = useState('');
  const [filterCliente, setFilterCliente] = useState('');
  const [filterEquipamento, setFilterEquipamento] = useState('');
  const [filterDescricao, setFilterDescricao] = useState('');
  const [filterPorta, setFilterPorta] = useState<string>('');
  const [filterOlt, setFilterOlt] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('todos');
  const [filterSinal, setFilterSinal] = useState<string>('todos');
  const [sortBy, setSortBy] = useState<string>('olt');
  const [desprovisioningId, setDesprovisioningId] = useState<number | null>(null);
  const [getInfoLoadingId, setGetInfoLoadingId] = useState<number | null>(null);
  const [duplicadasModalOpen, setDuplicadasModalOpen] = useState(false);
  const [onusModalOpen, setOnusModalOpen] = useState(false);
  const [selectedOnuForPort, setSelectedOnuForPort] = useState<ONU | null>(null);

  const fetchAllOnus = async () => {
    setLoading(true);
    try {
      const response = await api.get('/olts/onus');
      const data = response.data?.data ?? response.data;
      const list = Array.isArray(data) ? data : [];
      setOnus(list);
    } catch (err: any) {
      console.error('Erro ao carregar ONUs:', err);
      toast.error(err.response?.data?.message || 'Erro ao carregar ONUs');
      setOnus([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllOnus();
  }, []);

  useEffect(() => {
    if (refreshTrigger === 0) return;
    fetchAllOnus();
  }, [refreshTrigger]);

  const handleGetInfo = async (onu: ONU) => {
    setGetInfoLoadingId(onu.id);
    try {
      const response = await api.post('/olts/onus/get-info', { id: onu.id }) as any;
      const debugResult = response.data?.debug_result ?? response.data?.details ?? 'Sem dados retornados';
      const data = response.data?.data;
      let html = `<pre style="text-align: left; background: #2b2b2b; color: #fff; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto; max-height: 500px; white-space: pre-wrap;">${String(debugResult).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`;
      if (data && Object.keys(data).length > 0) {
        const dataStr = JSON.stringify(data, null, 2);
        html = `<pre style="text-align: left; background: #1e3a5f; color: #e0e0e0; padding: 10px; border-radius: 5px; font-size: 11px; margin-bottom: 10px;">${dataStr.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>${html}`;
      }
      await Swal.fire({
        title: response.data?.success ? '📈 Informações da ONU' : 'Erro ao obter informações',
        html,
        width: '700px',
        customClass: { container: 'swal-zindex swal-onu-info', popup: 'swal-onu-info' }
      });
    } catch (err: any) {
      const errorData = err.response?.data;
      const debugResult = errorData?.debug_result ?? errorData?.error ?? err.message ?? 'Erro desconhecido';
      await Swal.fire({
        title: 'Erro ao obter informações',
        html: `<p style="text-align: left; margin-bottom: 10px;">${errorData?.message || 'Falha ao consultar a OLT.'}</p>
               <pre style="text-align: left; background: #2b2b2b; color: #fff; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto;">${String(debugResult).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`,
        icon: 'error',
        width: '600px',
        customClass: { container: 'swal-zindex swal-onu-info', popup: 'swal-onu-info' }
      });
    } finally {
      setGetInfoLoadingId(null);
    }
  };

  const handleDesprovisionar = async (onu: ONU) => {
    if (!window.confirm(`Desprovisionar ONU ${onu.onu_id} (${onu.serialNumber}) da porta ${formatPorta(onu)}?`)) return;
    setDesprovisioningId(onu.id);
    try {
      const response = await api.post('/olts/onus/desprovisionar', { id: onu.id }) as any;
      if (response.data?.success) {
        toast.success('ONU desprovisionada com sucesso');
        await fetchAllOnus();
      } else {
        toast.error(response.data?.message || 'Erro ao desprovisionar ONU');
      }
    } catch (err: any) {
      console.error('❌ Erro ao desprovisionar ONU:', err);
      const errorData = err.response?.data;

      if (errorData?.debug_result) {
        await Swal.fire({
          title: 'Erro de Desprovisionamento',
          html: `<p style="text-align: left; margin-bottom: 10px;">${errorData.message || 'Falha ao desprovisionar na OLT.'}</p>
                 <pre style="text-align: left; background: #2b2b2b; color: #fff; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto;">${errorData.debug_result}</pre>`,
          icon: 'error',
          width: '600px',
          customClass: {
            container: 'swal-zindex'
          }
        });
      } else {
        toast.error(errorData?.message || 'Erro ao desprovisionar ONU');
      }
    } finally {
      setDesprovisioningId(null);
    }
  };

  const formatPorta = (onu: ONU) => {
    if (onu.sfp) return onu.sfp;
    if (onu.slot != null && onu.portNumber != null) return `${onu.slot}/${onu.portNumber}`;
    return '—';
  };

  const portOptions = useMemo(() => {
    const labels = onus.map(formatPorta).filter((p) => p !== '—');
    return Array.from(new Set(labels)).sort();
  }, [onus]);

  const stats = useMemo(() => {
    const online = onus.filter((o) => (o.status ?? '').toLowerCase() === 'online').length;
    const offline = onus.filter((o) => (o.status ?? '').toLowerCase() !== 'online').length;

    const sinalBom = onus.filter((o) => o.rxpower != null && o.rxpower > -23).length;
    const sinalRazoavel = onus.filter((o) => o.rxpower != null && o.rxpower >= -27 && o.rxpower <= -23).length;
    const sinalFraco = onus.filter((o) => o.rxpower != null && o.rxpower < -27).length;

    const porOlt: { name: string; count: number }[] = [];
    const oltCounts = new Map<string, number>();
    for (const o of onus) {
      const name = o.olt_name || '—';
      oltCounts.set(name, (oltCounts.get(name) ?? 0) + 1);
    }
    oltCounts.forEach((count, name) => porOlt.push({ name, count }));
    porOlt.sort((a, b) => a.name.localeCompare(b.name));

    const serialCounts = new Map<string, number>();
    for (const o of onus) {
      const sn = (o.serialNumber ?? '').trim();
      if (sn) serialCounts.set(sn, (serialCounts.get(sn) ?? 0) + 1);
    }
    // Serial duplicado = mesma ONU (GPON) registrada em 2+ lugares. Conta registros extras (ex: 3 registros = 2 duplicadas)
    const duplicadas = [...serialCounts.values()]
      .filter((c) => c > 1)
      .reduce((acc, c) => acc + (c - 1), 0);
    const serialsDuplicados = [...serialCounts.entries()]
      .filter(([, c]) => c > 1)
      .map(([serial, count]) => ({ serial, count }))
      .sort((a, b) => b.count - a.count);

    return { online, offline, sinalBom, sinalRazoavel, sinalFraco, porOlt, duplicadas, serialsDuplicados };
  }, [onus]);

  const filteredOnus = useMemo(() => {
    const serial = filterSerial.trim().toLowerCase();
    const cliente = filterCliente.trim().toLowerCase();
    const equipamento = filterEquipamento.trim().toLowerCase();
    const descricao = filterDescricao.trim().toLowerCase();
    const porta = filterPorta.trim();
    const olt = filterOlt.trim().toLowerCase();
    const status = filterStatus;

    let filtered = onus.filter((onu) => {
      if (serial && !(onu.serialNumber ?? '').toLowerCase().includes(serial)) return false;
      if (cliente && !(onu.cliente?.nome ?? '').toLowerCase().includes(cliente)) return false;
      const eqLabel = onu.equipamento
        ? `${onu.equipamento.marca} ${onu.equipamento.modelo} ${onu.equipamento.gpon_sn ?? ''}`.toLowerCase()
        : '';
      if (equipamento && !eqLabel.includes(equipamento)) return false;
      const desc = (onu.descricao || onu.description || '').toLowerCase();
      if (descricao && !desc.includes(descricao)) return false;
      if (porta && formatPorta(onu) !== porta) return false;
      if (olt && !(onu.olt_name ?? '').toLowerCase().includes(olt)) return false;
      if (status !== 'todos') {
        const onuStatus = onu.status?.toLowerCase() || 'offline';
        if (status === 'offline') {
          if (onuStatus === 'online') return false;
        } else if (onuStatus !== status) return false;
      }
      if (filterSinal !== 'todos') {
        if (onu.rxpower == null) return false;
        if (filterSinal === 'bom') return onu.rxpower > -23;
        if (filterSinal === 'razoavel') return onu.rxpower >= -27 && onu.rxpower <= -23;
        if (filterSinal === 'fraco') return onu.rxpower < -27;
      }
      return true;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'olt':
          return (a.olt_name || '').localeCompare(b.olt_name || '');
        case 'serial':
          return (a.serialNumber || '').localeCompare(b.serialNumber || '');
        case 'porta':
          return formatPorta(a).localeCompare(formatPorta(b));
        case 'rxpower':
          return (a.rxpower || -100) - (b.rxpower || -100);
        case 'txpower':
          return (a.txpower || -100) - (b.txpower || -100);
        case 'distance':
          return (a.distance || 0) - (b.distance || 0);
        case 'status':
          return (a.status || 'offline').localeCompare(b.status || 'offline');
        case 'onu_id':
        default:
          return (a.onu_id || 0) - (b.onu_id || 0);
      }
    });

    return filtered;
  }, [onus, filterSerial, filterCliente, filterEquipamento, filterDescricao, filterPorta, filterOlt, filterStatus, filterSinal, sortBy]);

  const getSignalStrengthColor = (signal: number) => {
    if (signal > -23) return 'success';   // Bom
    if (signal >= -27) return 'warning';  // Razoável
    return 'error';                       // Fraco
  };

  return (
    <Box>
      {onus.length > 0 && (
        <>
          <Paper sx={{ p: 1.5, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
              Estatísticas
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4} md={2}>
                <Paper variant="outlined" sx={{ p: 1, height: '100%' }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Status
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setFilterStatus('online')}
                      >
                        <TableCell sx={{ py: 0.25, fontSize: '0.75rem', border: 'none' }}>Online</TableCell>
                        <TableCell align="right" sx={{ py: 0.25, fontSize: '0.75rem', fontWeight: 600, color: 'success.main', border: 'none' }}>{stats.online}</TableCell>
                      </TableRow>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setFilterStatus('offline')}
                      >
                        <TableCell sx={{ py: 0.25, fontSize: '0.75rem', border: 'none' }}>Offline</TableCell>
                        <TableCell align="right" sx={{ py: 0.25, fontSize: '0.75rem', fontWeight: 600, border: 'none' }}>{stats.offline}</TableCell>
                      </TableRow>
                      <TableRow
                        hover
                        sx={{ cursor: stats.duplicadas > 0 ? 'pointer' : 'default' }}
                        onClick={() => stats.duplicadas > 0 && setDuplicadasModalOpen(true)}
                      >
                        <TableCell sx={{ py: 0.25, fontSize: '0.75rem', border: 'none' }}>Duplicadas</TableCell>
                        <TableCell align="right" sx={{ py: 0.25, fontSize: '0.75rem', fontWeight: 600, color: stats.duplicadas > 0 ? 'warning.main' : 'text.secondary', border: 'none' }}>{stats.duplicadas}</TableCell>
                      </TableRow>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setFilterStatus('todos')}
                      >
                        <TableCell sx={{ py: 0.25, fontSize: '0.75rem', border: 'none' }}>Total</TableCell>
                        <TableCell align="right" sx={{ py: 0.25, fontSize: '0.75rem', fontWeight: 600, border: 'none' }}>{onus.length}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Paper variant="outlined" sx={{ p: 1, height: '100%' }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Sinal (Rx)
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, py: 0.25, fontSize: '0.75rem' }}>Classificação</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.25, fontSize: '0.75rem' }}>Sinal (dBm)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, py: 0.25, fontSize: '0.75rem' }}>Qtd</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          setFilterSerial('');
                          setFilterCliente('');
                          setFilterEquipamento('');
                          setFilterDescricao('');
                          setFilterPorta('');
                          setFilterOlt('');
                          setFilterStatus('todos');
                          setFilterSinal('bom');
                        }}
                      >
                        <TableCell sx={{ py: 0.25, fontSize: '0.75rem' }}>Bom</TableCell>
                        <TableCell sx={{ py: 0.25, fontSize: '0.7rem', color: 'success.main' }}>&lt; -23</TableCell>
                        <TableCell align="right" sx={{ py: 0.25, fontSize: '0.75rem', color: 'success.main', fontWeight: 600 }}>{stats.sinalBom}</TableCell>
                      </TableRow>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          setFilterSerial('');
                          setFilterCliente('');
                          setFilterEquipamento('');
                          setFilterDescricao('');
                          setFilterPorta('');
                          setFilterOlt('');
                          setFilterStatus('todos');
                          setFilterSinal('razoavel');
                        }}
                      >
                        <TableCell sx={{ py: 0.25, fontSize: '0.75rem' }}>Razoável</TableCell>
                        <TableCell sx={{ py: 0.25, fontSize: '0.7rem', color: 'warning.main' }}>-23 a -27</TableCell>
                        <TableCell align="right" sx={{ py: 0.25, fontSize: '0.75rem', color: 'warning.main', fontWeight: 600 }}>{stats.sinalRazoavel}</TableCell>
                      </TableRow>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          setFilterSerial('');
                          setFilterCliente('');
                          setFilterEquipamento('');
                          setFilterDescricao('');
                          setFilterPorta('');
                          setFilterOlt('');
                          setFilterStatus('todos');
                          setFilterSinal('fraco');
                        }}
                      >
                        <TableCell sx={{ py: 0.25, fontSize: '0.75rem' }}>Fraco</TableCell>
                        <TableCell sx={{ py: 0.25, fontSize: '0.7rem', color: 'error.main' }}>-27 &gt;</TableCell>
                        <TableCell align="right" sx={{ py: 0.25, fontSize: '0.75rem', color: 'error.main', fontWeight: 600 }}>{stats.sinalFraco}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper variant="outlined" sx={{ p: 1, height: '100%' }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Por OLT
                  </Typography>
                  <TableContainer sx={{ maxHeight: 120 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, py: 0.25, fontSize: '0.75rem' }}>OLT</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, py: 0.25, fontSize: '0.75rem' }}>ONUs</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.porOlt.map(({ name, count }) => (
                          <TableRow key={name}>
                            <TableCell sx={{ py: 0.25, fontSize: '0.75rem' }}>{name}</TableCell>
                            <TableCell align="right" sx={{ py: 0.25, fontSize: '0.75rem' }}>{count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <FilterIcon color="action" fontSize="small" />
              <Typography variant="subtitle2" color="text.secondary">
                Filtros
              </Typography>
            </Box>
          <Box
            display="flex"
            flexWrap="wrap"
            gap={2}
            alignItems="flex-start"
          >
            <TextField
              size="small"
              label="OLT"
              value={filterOlt}
              onChange={(e) => setFilterOlt(e.target.value)}
              placeholder="Buscar por OLT..."
              sx={{ minWidth: 160 }}
            />
            <TextField
              size="small"
              label="Serial (GPON)"
              value={filterSerial}
              onChange={(e) => setFilterSerial(e.target.value)}
              placeholder="Buscar por serial..."
              sx={{ minWidth: 180 }}
            />
            <TextField
              size="small"
              label="Cliente"
              value={filterCliente}
              onChange={(e) => setFilterCliente(e.target.value)}
              placeholder="Buscar por cliente..."
              sx={{ minWidth: 180 }}
            />
            <TextField
              size="small"
              label="Equipamento"
              value={filterEquipamento}
              onChange={(e) => setFilterEquipamento(e.target.value)}
              placeholder="Marca, modelo ou serial..."
              sx={{ minWidth: 180 }}
            />
            <TextField
              size="small"
              label="Descrição"
              value={filterDescricao}
              onChange={(e) => setFilterDescricao(e.target.value)}
              placeholder="Buscar na descrição..."
              sx={{ minWidth: 180 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="filter-porta-label">Porta</InputLabel>
              <Select
                labelId="filter-porta-label"
                value={filterPorta}
                label="Porta"
                onChange={(e) => setFilterPorta(e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                {portOptions.map((porta) => (
                  <MenuItem key={porta} value={porta}>
                    {porta}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="filter-status-label">Status</InputLabel>
              <Select
                labelId="filter-status-label"
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="down">Down</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="filter-sinal-label">Sinal</InputLabel>
              <Select
                labelId="filter-sinal-label"
                value={filterSinal}
                label="Sinal"
                onChange={(e) => setFilterSinal(e.target.value)}
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="bom">Bom &lt; -23</MenuItem>
                <MenuItem value="razoavel">Razoável -23 a -27</MenuItem>
                <MenuItem value="fraco">Fraco -27 &gt;</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="sort-by-label">Ordenar por</InputLabel>
              <Select
                labelId="sort-by-label"
                value={sortBy}
                label="Ordenar por"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="olt">OLT (Padrão)</MenuItem>
                <MenuItem value="porta">Porta</MenuItem>
                <MenuItem value="onu_id">ONU ID</MenuItem>
                <MenuItem value="serial">Serial Number</MenuItem>
                <MenuItem value="rxpower">Sinal (Rx Power)</MenuItem>
                <MenuItem value="txpower">Sinal (Tx Power)</MenuItem>
                <MenuItem value="distance">Distância</MenuItem>
                <MenuItem value="status">Status</MenuItem>
              </Select>
            </FormControl>
          </Box>
          </Paper>
        </>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : onus.length > 0 ? (
        <>
          {filteredOnus.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Nenhuma ONU corresponde aos filtros. Ajuste os critérios ou limpe os filtros.
            </Alert>
          ) : (
            <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <TableContainer sx={{ maxHeight: 520 }}>
                <Table size="small" stickyHeader sx={{ '& .MuiTableBody-root .MuiTableCell-root': { py: 0.5, fontSize: '0.78rem' } }}>
                  <TableHead>
                    <TableRow sx={{
                      '& .MuiTableCell-head': {
                        bgcolor: (t) => `color-mix(in srgb, ${primario} 12%, ${t.palette.background.paper})`,
                        fontWeight: 700,
                        fontSize: '0.73rem',
                        py: 0.8,
                        borderBottom: `2px solid ${alpha(primario, 0.2)}`,
                      },
                    }}>
                      <TableCell>OLT</TableCell>
                      <TableCell>ONU ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Serial (GPON)</TableCell>
                      <TableCell>Porta</TableCell>
                      <TableCell>IP/Rede</TableCell>
                      <TableCell>Sinal (Rx/Tx)</TableCell>
                      <TableCell>Distância</TableCell>
                      <TableCell>Descrição</TableCell>
                      <TableCell>Service Port</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Equipamento</TableCell>
                      <TableCell align="center">Ação</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOnus.map((onu) => (
                    <TableRow key={onu.id} hover sx={{ '&:hover': { bgcolor: alpha(primario, 0.03) } }}>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>
                          {onu.olt_name || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {onu.onu_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={onu.status === 'online' ? 'Online' : onu.status === 'down' ? 'Down' : onu.status || 'Offline'}
                          size="small"
                          color={onu.status === 'online' ? 'success' : onu.status === 'down' ? 'error' : 'default'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {onu.serialNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatPorta(onu)}
                          color="primary"
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            if (onu.portId && onu.olt_id) {
                              setSelectedOnuForPort(onu);
                              setOnusModalOpen(true);
                            }
                          }}
                          sx={{ cursor: onu.portId && onu.olt_id ? 'pointer' : 'default' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {onu.ipv4_address || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.8rem',
                            color: onu.rxpower != null ? `${getSignalStrengthColor(onu.rxpower)}.main` : 'text.secondary'
                          }}
                        >
                          {onu.rxpower != null ? `${onu.rxpower} dBm` : '—'} / {onu.txpower != null ? `${onu.txpower} dBm` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {onu.distance != null ? `${onu.distance} m` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {onu.descricao || onu.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          [{onu.services_port != null ? String(onu.services_port).replace(/,/g, ', ') : '—'}]
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {onu.cliente ? (
                          <Typography variant="body2">{onu.cliente.nome}</Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {onu.equipamento ? (
                          <Link
                            component={RouterLink}
                            to={`/equipments/${onu.equipamento.id}`}
                            variant="body2"
                          >
                            {onu.equipamento.marca} {onu.equipamento.modelo}
                          </Link>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <IconButton
                          color="info"
                          size="small"
                          aria-label="Obter informações"
                          onClick={() => handleGetInfo(onu)}
                          disabled={getInfoLoadingId !== null}
                          sx={{ fontSize: '0.75rem', mr: 0.5 }}
                        >
                          {getInfoLoadingId === onu.id ? <CircularProgress size={18} /> : <InfoIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          aria-label="Desprovisionar"
                          onClick={() => handleDesprovisionar(onu)}
                          disabled={desprovisioningId !== null}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {desprovisioningId === onu.id ? <CircularProgress size={18} /> : <DesprovisionIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </Paper>
          )}
        </>
      ) : (
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
          <OnuIcon color="action" sx={{ fontSize: 64 }} />
          <Typography variant="h6" color="text.secondary">
            Nenhuma ONU registrada no sistema
          </Typography>
          <Typography variant="body2" color="text.secondary">
            As ONUs aparecem aqui após serem sincronizadas nas portas das OLTs. Acesse a aba OLTs e sincronize as portas.
          </Typography>
        </Box>
      )}

      <OnusModal
        open={onusModalOpen}
        onClose={() => {
          setOnusModalOpen(false);
          setSelectedOnuForPort(null);
        }}
        oltId={selectedOnuForPort?.olt_id?.toString()}
        cardSlot={selectedOnuForPort?.slot}
        portNumber={selectedOnuForPort?.portNumber}
        portId={selectedOnuForPort?.portId}
        cardId={selectedOnuForPort?.cardId}
      />

      <OnuDuplicadasModal
        open={duplicadasModalOpen}
        onClose={() => setDuplicadasModalOpen(false)}
        serialsDuplicados={stats.serialsDuplicados}
        onSerialClick={(serial) => {
          setFilterSerial(serial);
          setFilterCliente('');
          setFilterEquipamento('');
          setFilterDescricao('');
          setFilterPorta('');
          setFilterOlt('');
          setFilterStatus('todos');
          setFilterSinal('todos');
        }}
      />
    </Box>
  );
};

export default OnusRegistradasTab;
